import { FormattedMessage, useIntl } from 'react-intl';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Paper from '@mui/material/Paper';
import SmartphoneOutlinedIcon from '@mui/icons-material/SmartphoneOutlined';
import CloudOutlinedIcon from '@mui/icons-material/CloudOutlined';
import DiffView from '../../components/DiffView';

export type ConflictSide = 'local' | 'remote';

interface ConflictFieldRowProps {
    label: string;
    isRichText: boolean;
    /** Each side as a person reads it — links resolved, lists spelled out. */
    localText: string;
    remoteText: string;
    selected: ConflictSide;
    onChange: (side: ConflictSide) => void;
}

/**
 * Local (this device) is coloured `info`, the server `warning`, consistently
 * with the dialog's action buttons.
 */
const LOCAL_COLOR = 'info.main';
const REMOTE_COLOR = 'warning.main';

export default function ConflictFieldRow({
    label,
    isRichText,
    localText,
    remoteText,
    selected,
    onChange
}: ConflictFieldRowProps) {
    const intl = useIntl();

    return (
        <Box sx={{ mb: 3 }}>
            <Typography
                variant="subtitle1"
                sx={{
                    fontWeight: 'bold',
                    mb: 1
                }}
            >
                {label}
            </Typography>
            <Box sx={{ mb: 2 }}>
                <Typography
                    variant="caption"
                    sx={{
                        color: 'text.secondary',
                        mb: 0.5,
                        display: 'block'
                    }}
                >
                    <FormattedMessage id="conflict.changesLabel" />
                </Typography>
                <DiffView
                    oldValue={remoteText}
                    newValue={localText}
                    isHtml={isRichText}
                />
            </Box>
            <RadioGroup
                value={selected}
                onChange={(event) =>
                    onChange(event.target.value as ConflictSide)
                }
            >
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Paper
                        variant="outlined"
                        sx={{
                            flex: 1,
                            p: 2,
                            borderColor:
                                selected === 'local' ? LOCAL_COLOR : 'divider',
                            borderWidth: selected === 'local' ? 2 : 1
                        }}
                    >
                        <FormControlLabel
                            value="local"
                            control={<Radio color="info" />}
                            label={
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        color: LOCAL_COLOR
                                    }}
                                >
                                    <SmartphoneOutlinedIcon fontSize="small" />
                                    <FormattedMessage id="conflict.yourVersion" />
                                </Box>
                            }
                        />
                        {isRichText ? (
                            <Box
                                sx={{ mt: 1, fontSize: '0.875rem' }}
                                dangerouslySetInnerHTML={{
                                    __html: localText
                                }}
                            />
                        ) : (
                            <Typography variant="body2" sx={{ mt: 1 }}>
                                {localText ||
                                    intl.formatMessage({ id: 'common.empty' })}
                            </Typography>
                        )}
                    </Paper>

                    <Paper
                        variant="outlined"
                        sx={{
                            flex: 1,
                            p: 2,
                            borderColor:
                                selected === 'remote'
                                    ? REMOTE_COLOR
                                    : 'divider',
                            borderWidth: selected === 'remote' ? 2 : 1
                        }}
                    >
                        <FormControlLabel
                            value="remote"
                            control={<Radio color="warning" />}
                            label={
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        color: REMOTE_COLOR
                                    }}
                                >
                                    <CloudOutlinedIcon fontSize="small" />
                                    <FormattedMessage id="conflict.serverVersion" />
                                </Box>
                            }
                        />
                        {isRichText ? (
                            <Box
                                sx={{ mt: 1, fontSize: '0.875rem' }}
                                dangerouslySetInnerHTML={{
                                    __html: remoteText
                                }}
                            />
                        ) : (
                            <Typography variant="body2" sx={{ mt: 1 }}>
                                {remoteText ||
                                    intl.formatMessage({ id: 'common.empty' })}
                            </Typography>
                        )}
                    </Paper>
                </Box>
            </RadioGroup>
        </Box>
    );
}
